/**
 * Custom hook: opens a WebSocket to the messages room for a job and falls back
 * to HTTP polling on failure. Returns the message list, live indicator, and a
 * send function. Extracted from MessageDrawer to reduce component complexity.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";

const POLL_INTERVAL_MS = 4000;

export function useMessageWebSocket(jobId) {
  const [messages, setMessages] = useState([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/messages/job/${jobId}`);
      setMessages(r.data);
    } catch (err) {
      logger.error("[messages] history load failed", err);
    }
  }, [jobId]);

  // Open WS + history on mount/jobId change
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    let pollerStarted = false;

    const startPolling = () => {
      if (pollerStarted) return;
      pollerStarted = true;
      pollRef.current = setInterval(loadHistory, POLL_INTERVAL_MS);
    };

    loadHistory();

    // With httpOnly cookie auth, the browser auto-sends the auth_token cookie on
    // same-origin WS handshake. We keep a `?token=` query param for legacy/non-cookie
    // contexts (curl, mobile native) — backend accepts both.
    const wsBase = API.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/ws/messages/${jobId}`);
    wsRef.current = ws;

    ws.onopen = () => { if (!cancelled) setLive(true); };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        setMessages((prev) => (prev.some((m) => m.message_id === msg.message_id) ? prev : [...prev, msg]));
      } catch (err) {
        logger.error("[messages] WS parse failed", err);
      }
    };
    ws.onerror = (err) => {
      logger.warn("[messages] WS error, falling back to polling", err);
      setLive(false);
      startPolling();
    };
    ws.onclose = () => {
      if (cancelled) return;
      setLive(false);
      startPolling();
    };

    return () => {
      cancelled = true;
      try { ws.close(); } catch (err) { logger.error("[messages] WS close failed", err); }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobId, loadHistory]);

  const send = useCallback(async (text, toUserId) => {
    const t = text.trim();
    if (!t) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ text: t, to_user_id: toUserId || "" }));
      return;
    }
    try {
      await api.post("/messages", { job_id: jobId, to_user_id: toUserId || "", text: t });
      await loadHistory();
    } catch (err) {
      logger.error("[messages] send failed", err);
      throw err;
    }
  }, [jobId, loadHistory]);

  return { messages, live, send };
}
